import { useEffect, useState } from "react";
import { PageHeader, EmptyState, Pill } from "@/components/ui-bits";
import { Trash2, Clock, FileQuestion, Download, Repeat, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";
import api, { API_BASE } from "@/lib/api";

export default function AssessmentsAdmin() {
  const [items, setItems] = useState([]);
  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [toDelete, setToDelete] = useState(null); // assessment object pending delete
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    api.get("/assessments").then(r => setItems(r.data));
    api.get("/batches").then(r => setBatches(r.data));
    api.get("/courses").then(r => setCourses(r.data));
  };
  useEffect(load, []);

  const openDelete = (a) => { setToDelete(a); setConfirmText(""); };
  const closeDelete = () => { if (!deleting) { setToDelete(null); setConfirmText(""); } };

  const canConfirm = toDelete && confirmText.trim().toUpperCase() === "DELETE";

  const doDelete = async () => {
    if (!canConfirm) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/assessments/${toDelete.id}`);
      toast.success(`Deleted “${data.title || toDelete.title}”`);
      setToDelete(null);
      setConfirmText("");
      load();
    } catch (err) {
      const detail = err.response?.data?.detail || "Failed to delete assessment";
      toast.error(typeof detail === "string" ? detail : "Failed to delete assessment");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <PageHeader title="Assessments" subtitle="All tests configured across the institute. Only admins can delete tests." />
      {items.length === 0 ? <EmptyState title="No assessments yet" hint="Trainers can create assessments from the Trainer Hub." /> : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map(a => {
            const course = courses.find(c => c.id === a.course_id);
            const batch = batches.find(b => b.id === a.batch_id);
            const allow = a.allow_reattempts !== false;
            const maxAtt = a.max_attempts;
            const reattemptLabel = !allow
              ? "Single attempt"
              : maxAtt
                ? `Up to ${maxAtt} attempts`
                : "Unlimited attempts";
            return (
              <div key={a.id} data-testid={`assessment-card-${a.id}`} className="bg-white border border-slate-200 rounded-xl p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-lg font-semibold text-slate-900">{a.title}</div>
                    <div className="mt-1 text-xs text-slate-500">{course?.course_name || "All courses"} · {batch?.batch_name || "All batches"}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <a
                      data-testid={`assessment-export-${a.id}`}
                      href={`${API_BASE}/assessments/${a.id}/export-questions`}
                      title="Export questions as CSV"
                      className="text-slate-600 hover:bg-slate-100 rounded-lg p-2">
                      <Download className="w-4 h-4" />
                    </a>
                    <button
                      data-testid={`assessment-delete-${a.id}`}
                      onClick={() => openDelete(a)}
                      title="Delete assessment (admin only)"
                      className="text-red-600 hover:bg-red-50 rounded-lg p-2">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2 text-sm text-slate-600">
                  <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4 text-slate-400" /> {a.duration} min</span>
                  <span className="inline-flex items-center gap-1.5"><FileQuestion className="w-4 h-4 text-slate-400" /> {a.question_ids?.length || 0} questions</span>
                  <Pill tone="blue">Pass ≥ {a.passing_marks}%</Pill>
                  <Pill tone={allow ? "green" : "amber"}>
                    <Repeat className="inline-block w-3 h-3 mr-1 -mt-0.5" />
                    {reattemptLabel}
                  </Pill>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {toDelete && (
        <div
          data-testid="delete-confirm-modal"
          className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={closeDelete}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-full bg-red-100 grid place-items-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h2 className="text-lg font-semibold text-slate-900">Delete this test?</h2>
                    <button onClick={closeDelete} className="text-slate-400 hover:text-slate-600 -mt-1">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="mt-1.5 text-sm text-slate-600">
                    You are about to permanently delete
                    {" "}<span className="font-semibold text-slate-900">“{toDelete.title}”</span>.
                    This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="mt-5 bg-red-50 border border-red-200 rounded-lg p-3 text-xs text-red-800 space-y-1">
                <div className="font-semibold uppercase tracking-wider text-red-900">This will</div>
                <ul className="list-disc list-inside space-y-0.5">
                  <li>Remove the test from all admin, trainer and employee views</li>
                  <li>Prevent any pending attempts from being started</li>
                  <li>Preserve past results in Reports for audit purposes</li>
                </ul>
              </div>

              <div className="mt-5">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Type <code className="font-mono-acc text-red-700 font-semibold">DELETE</code> to confirm
                </label>
                <input
                  data-testid="delete-confirm-input"
                  autoFocus
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && canConfirm) doDelete(); }}
                  placeholder="DELETE"
                  className="w-full border border-slate-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none uppercase tracking-widest"
                />
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button
                  data-testid="delete-cancel"
                  onClick={closeDelete}
                  disabled={deleting}
                  className="px-4 py-2 border border-slate-300 hover:bg-slate-50 rounded-lg text-sm font-medium disabled:opacity-50">
                  Cancel
                </button>
                <button
                  data-testid="delete-confirm"
                  onClick={doDelete}
                  disabled={!canConfirm || deleting}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed">
                  <Trash2 className="w-4 h-4" />
                  {deleting ? "Deleting…" : "Delete test"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
